import Link from 'next/link';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { RouteE } from 'src/constants/pages.constants';
import { NftCard } from 'src/features/common/collectible/components/nft-card';
import { useBannerNfts } from 'src/features/common/collectible/hooks/use-banner-nfts';
import { mapSorting } from 'src/features/common/explore/explore-api.utils';
import {
    ISectionConfiguration,
    WizardStateBanner,
} from 'src/features/common/universe-wizard/context/wizard-context.types';
import { useImageUrlCheck } from 'src/hooks/useImageUrlCheck';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { Typography } from 'src/ui/common/atoms/typography';
import { canvasAnimationSpeed } from 'src/ui/common/config/animations';
import { useGradientAnimation } from 'src/ui/common/hooks/useGradientAnimation.hook';
import { getUrlWithPublicStorage } from 'src/utils/get-url-with-public-host';
import {
    StyledCanvas,
    StyledDescription,
    StyledInfoBlock,
    StyledTopBanner,
    StyledWrapper,
    StyledCarousel,
} from './universe-top-banner.styles';

export const UniverseTopBannerContainer = ({
    title,
    description,
    type,
    limit,
    sorting,
    image,
}: ISectionConfiguration<WizardStateBanner>) => {
    const { t } = useTranslation(['pages']);
    const screenType = useScreenDetect();
    const { fetch, clear, bannerNfts, isLoading } = useBannerNfts();
    const { ref: canvasRef, entry: canvas, inView } = useInView();
    const { startAnimation, stopAnimation } = useGradientAnimation();
    const bannerImage = useImageUrlCheck({ imageUrl: getUrlWithPublicStorage(image) });
    const isSliderType = type === 'slider';

    const titleFont = useMemo(() => {
        if (screenType.xs) {
            return 'md';
        }
        if (screenType.sm) {
            return 'lg';
        }
        return 'xl';
    }, [screenType]);

    useEffect(() => {
        if (isSliderType) {
            fetch({ pager: { limit, page: 0 }, sorting: mapSorting(sorting!) });
            return clear;
        }
        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (inView && !bannerImage) {
            startAnimation(canvas!.target as HTMLCanvasElement, canvasAnimationSpeed.slow);
        }
        if (!inView && !bannerImage) {
            stopAnimation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, canvas, bannerImage]);

    const Carousel = useMemo(() => {
        if (isLoading) {
            return null;
        }
        return (
            <StyledCarousel
                lg={1}
                md={1}
                sm={1}
                xl={1}
                xs={1}
                slidesToShow={1}
                slidesToScroll={1}
                centerPadding="0px"
                autoplay={true}
                autoplaySpeed={3000}
                infinite={true}
                initialSlide={bannerNfts.length > 2 ? 1 : 0}
                adaptiveHeight
                variableWidth
                centerMode
                dots
            >
                {bannerNfts!.map((nft) => (
                    <NftCard nft={nft} key={nft.id} variant="light" />
                ))}
            </StyledCarousel>
        );
    }, [bannerNfts, isLoading]);

    return (
        <StyledTopBanner img={bannerImage}>
            {!bannerImage && <StyledCanvas ref={canvasRef} width="32" height="32" />}
            <StyledWrapper slidesCount={bannerNfts.length}>
                <StyledInfoBlock>
                    <div>
                        <Typography size={titleFont} variant="title" color="text">
                            {title}
                        </Typography>
                        <StyledDescription
                            size={screenType.xs || screenType.sm ? 'xs' : 'sm'}
                            variant="text"
                            color="gs200"
                        >
                            <span dangerouslySetInnerHTML={{ __html: description! }} />
                        </StyledDescription>
                    </div>
                    <Link href={RouteE.EXPLORE} prefetch={false}>
                        <Button variant="white" size={screenType.xs || screenType.sm ? 'md' : 'lg'}>
                            {t('home.widgets.topBanner.universeButton')}
                        </Button>
                    </Link>
                </StyledInfoBlock>
                {isSliderType && Carousel}
            </StyledWrapper>
        </StyledTopBanner>
    );
};
